import React from 'react';
import { Spinner } from 'react-bootstrap';
import { BORDER_COLORS } from '../utils/constants';
import { fmtUnit } from '../utils/helpers';
import InfoTip from './InfoTip';

// unit : respecte le sélecteur d'unité de la section parente (montants uniquement, pas les compteurs)

export const MiniPct = ({ pctVal }) => {
  if (pctVal == null) return <span style={{ color: '#ccc', fontSize: '0.68rem' }}>—</span>;
  const color = pctVal >= 100 ? '#2e7d32' : pctVal >= 70 ? '#e65100' : '#c62828';
  return <span style={{ color, fontWeight: 700, fontSize: '0.72rem' }}>{pctVal}%</span>;
};

export const ProjetKpiCard = ({ item, idx, onBudgetClick, onVentesClick, onRecClick, onPnClick, unit }) => {
  const border = BORDER_COLORS[idx % BORDER_COLORS.length];
  const rows = [
    { label: 'Vente',  info: 'CA Ventes réalisé sur ce projet : lots directs (TYPE_VENTE = UNIQUE) + parts d\'associations dans la période.', budget: item.budgetVente, real: item.caVentes,  pct: item.pctVente, color: '#6d4e36', onClick: onVentesClick },
    { label: 'Recouv', info: 'Encaissements supplémentaires (enc_supp = 1) reçus sur les contrats de ce projet dans la période.', budget: item.budgetRec,   real: item.realisRec, pct: item.pctRec,   color: '#2e7d32', onClick: onRecClick },
    { label: 'PN',     info: 'Montant des ventes de ce projet dont la date de passage notaire est dans la période sélectionnée.', budget: item.budgetPn,    real: item.realisPn,  pct: item.pctPn,    color: '#1b5e20', onClick: onPnClick },
  ];
  return (
    <div style={{ border: `2px solid ${border}`, borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.07)', background: '#fdfcf7', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontWeight: 700, color: '#3e2723', fontSize: '0.82rem', textAlign: 'center', marginBottom: 2 }}>
        🏢 {item.projet || `Projet ${item.projetId}`}
      </div>
      {rows.map(({ label, info, budget, real, pct: pctVal, color, onClick }) => (
        <div key={label} style={{ background: '#fff', borderRadius: 7, border: '1px solid #ece7e0', padding: '5px 9px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center' }}>{label}<InfoTip text={info} /></span>
            <MiniPct pctVal={pctVal} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: '0.88rem', fontWeight: 700, color, cursor: onClick ? 'pointer' : 'default', textDecoration: onClick ? 'underline dotted' : 'none' }}
              onClick={onClick} title={onClick ? 'Voir le détail' : undefined}>{fmtUnit(real, unit)}</span>
            {budget > 0 && (
              <span style={{ fontSize: '0.65rem', color: '#bbb', cursor: onBudgetClick ? 'pointer' : 'default', textDecoration: onBudgetClick ? 'underline dotted' : 'none' }}
                onClick={onBudgetClick ? () => onBudgetClick(item.projetId, item.projet) : undefined}
                title={onBudgetClick ? 'Voir le détail budget' : undefined}>/ {fmtUnit(budget, unit)}</span>
            )}
          </div>
          {budget > 0 && (
            <div style={{ marginTop: 4, height: 3, borderRadius: 2, background: '#ece7e0', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 2, background: color, width: `${Math.min(100, Math.round((real / budget) * 100))}%`, transition: 'width 0.4s ease' }} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const StackedBarProjet = ({ data, loading, unit }) => {
  if (loading) return <div style={{ padding: 30, textAlign: 'center' }}><Spinner /></div>;
  if (!data.length) return null;

  const SEGS = [
    { realKey: 'caVentes',  budKey: 'budgetVente', realColor: '#9e9e9e', budColor: '#e0e0e0', label: 'CA Ventes' },
    { realKey: 'realisPn',  budKey: 'budgetPn',    realColor: '#d4c09a', budColor: '#ede8da', label: 'Livraison PN' },
    { realKey: 'realisRec', budKey: 'budgetRec',   realColor: '#b07070', budColor: '#e8c8c8', label: 'Encaissements' },
  ];

  const maxVal = Math.max(
    ...data.map(r => Math.max(
      r.caVentes + r.realisPn + r.realisRec,
      (r.budgetVente || 0) + (r.budgetPn || 0) + (r.budgetRec || 0)
    )),
    1
  );

  const sorted = [...data].sort((a, b) => (b.caVentes + b.realisPn + b.realisRec) - (a.caVentes + a.realisPn + a.realisRec));

  return (
    <div style={{ overflowX: 'auto' }}>
      {/* Légende */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: 12, fontSize: '0.63rem', color: '#666', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, color: '#444' }}>Réalisé :</span>
        {SEGS.map(s => (
          <span key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.realColor, display: 'inline-block' }} />{s.label}
          </span>
        ))}
        <span style={{ color: '#ddd', margin: '0 4px' }}>|</span>
        <span style={{ fontWeight: 700, color: '#444' }}>Budget :</span>
        {SEGS.map(s => (
          <span key={`b-${s.label}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.budColor, border: '1.5px dashed #bbb', display: 'inline-block', boxSizing: 'border-box' }} />{s.label}
          </span>
        ))}
      </div>

      {/* Barres */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {sorted.map(r => {
          const realTotal = r.caVentes + r.realisPn + r.realisRec;
          const budTotal  = (r.budgetVente || 0) + (r.budgetPn || 0) + (r.budgetRec || 0);
          return (
            <div key={r.projetId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Nom projet */}
              <div style={{ width: 120, flexShrink: 0, textAlign: 'right', fontSize: '0.7rem', color: '#555', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.projet}
              </div>
              {/* Double barre */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 100 }}>
                {/* Réalisé */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '0.59rem', color: '#888', width: 28, flexShrink: 0, textAlign: 'right', fontWeight: 600 }}>Réal.</span>
                  <div style={{ flex: 1, height: 18, display: 'flex', borderRadius: 3, overflow: 'hidden', background: '#f0f0f0' }}>
                    {SEGS.map(s => {
                      const v = r[s.realKey] || 0;
                      return v > 0 ? <div key={s.realKey} style={{ width: `${Math.round((v / maxVal) * 100)}%`, background: s.realColor, transition: 'width 0.5s ease' }} title={`${s.label}: ${fmtUnit(v, unit)}`} /> : null;
                    })}
                  </div>
                  <span style={{ fontSize: '0.65rem', color: '#444', fontWeight: 700, flexShrink: 0, width: 62, textAlign: 'right' }}>{fmtUnit(realTotal, unit)}</span>
                </div>
                {/* Budget */}
                {budTotal > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: '0.59rem', color: '#bbb', width: 28, flexShrink: 0, textAlign: 'right' }}>Bud.</span>
                    <div style={{ flex: 1, height: 13, display: 'flex', borderRadius: 3, overflow: 'hidden', background: '#fafafa', border: '1px dashed #ddd' }}>
                      {SEGS.map(s => {
                        const v = r[s.budKey] || 0;
                        return v > 0 ? <div key={s.budKey} style={{ width: `${Math.round((v / maxVal) * 100)}%`, background: s.budColor, transition: 'width 0.5s ease' }} title={`Budget ${s.label}: ${fmtUnit(v, unit)}`} /> : null;
                      })}
                    </div>
                    <span style={{ fontSize: '0.65rem', color: '#aaa', flexShrink: 0, width: 62, textAlign: 'right' }}>{fmtUnit(budTotal, unit)}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
